import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { APIProvider, Map as GMap, AdvancedMarker, Pin, MapControl, ControlPosition } from '@vis.gl/react-google-maps';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { getPersonPhotoPath, calculateAge, parseAgeFilter, evaluateAgeCondition } from '../lib/utils';
import { AlertTriangle } from 'lucide-react';

const TIPOLOGIA_COLORS: Record<number, string> = {
  1: '#2563EB', // Casa / Residenza (Blue)
  2: '#10B981', // Lavoro / Sede Professionale (Emerald)
  3: '#8B5CF6', // Domicilio Fiscale / Fatturazione (Purple)
  4: '#F97316', // Spedizione / Consegna (Orange)
  5: '#EC4899', // Punto di Ritiro / Locker (Pink)
  6: '#06B6D4', // Dimora Temporanea (Cyan)
  7: '#F59E0B'  // Domicilio Digitale PEC (Amber/Yellow)
};

const TIPOLOGIA_LABELS: Record<number, string> = {
  1: 'Casa / Residenza',
  2: 'Lavoro / Sede Prof.',
  3: 'Domicilio Fiscale',
  4: 'Spedizione / Consegna',
  5: 'Punto di Ritiro',
  6: 'Dimora Temporanea',
  7: 'Domicilio Digitale PEC'
};

function getClusterCellSize(z: number) {
  if (z >= 17) return 0.00003; 
  if (z === 16) return 0.00008; 
  if (z === 15) return 0.00025; 
  if (z === 14) return 0.00075; 
  if (z === 13) return 0.0022;  
  if (z === 12) return 0.0065;  
  if (z === 11) return 0.015;  
  if (z === 10) return 0.035;  
  if (z === 9)  return 0.08;   
  if (z === 8)  return 0.16;    
  return 0.45; 
}
import { 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Filter, 
  Plus, 
  Trash2, 
  Edit2,
  X,
  Check,
  MoreVertical,
  ArrowUpDown,
  Download,
  Upload,
  FileSpreadsheet,
  FileJson,
  FileText,
  Printer,
  RefreshCw,
  Layout,
  Table as TableIcon,
  ArrowUpWideNarrow,
  ArrowDownWideNarrow,
  User,
  Grid,
  Map as MapIcon,
  Maximize2,
  Minimize2,
  List,
  Type,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useDataTable, DataTableOptions } from '../hooks/useDataTable';
import { useAuth } from '../hooks/useAuth';
import { useConfig } from '../hooks/useConfig';
import { cn } from '../lib/utils';
import { SignedImage } from './SignedImage';

const HighlightText: React.FC<{ text: string; highlight: string; color: string; isActive?: boolean }> = ({ text, highlight, color, isActive }) => {
  if (!highlight.trim()) return <>{text}</>;
  try {
    const parts = text.split(new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span 
              key={i} 
              style={{ 
                backgroundColor: isActive ? '#f59e0b' : color, 
                color: isActive ? 'white' : 'inherit'
              }} 
              className={cn(
                "rounded-sm px-0.5 font-bold shadow-[0_0_0_1px_rgba(0,0,0,0.05)] transition-all duration-300",
                isActive ? "ring-2 ring-orange-400 ring-offset-1 scale-110 z-10" : "text-slate-900"
              )}
            >
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  } catch (e) {
    return <>{text}</>;
  }
};

interface DBTableProps extends DataTableOptions {
  forceGeo?: boolean;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  pinnedColumns?: number;
  columnLabels?: Record<string, string>;
  virtualColumns?: string[];
  renderers?: Record<string, (val: any, row: any) => React.ReactNode>;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onAdd?: () => void;
  addLabel?: string;
  onAction?: (action: string, selectedIds: (string | number)[], state: 'download' | 'upload') => void;
  onRowDoubleClick?: (row: any) => void;
  gridAxis?: 'vertical' | 'horizontal' | 'both' | 'none';
  headerExtras?: React.ReactNode;
  initialData?: any[];
  onRefresh?: () => void;
  searchPlaceholder?: string;
}

export const DBTable: React.FC<DBTableProps> = ({ 
  tableName, 
  schema,
  whereClause,
  columns: initialColumns = [], 
  virtualColumns = [],
  pinnedColumns = 0,
  columnLabels = {},
  icon,
  sortable = true, 
  searchable = true, 
  filterable = true, 
  selectable = true,
  title,
  renderers = {},
  onView,
  onEdit,
  onAdd,
  addLabel,
  onAction,
  onRowDoubleClick,
  pageSize: initialPageSize,
  initialPage,
  limit,
  offset,
  gridAxis = 'horizontal',
  headerExtras,
  initialData, // Add this here
  adminMode = false,
  forceGeo = false,
  onRefresh,
  searchPlaceholder
}) => {
  const navigate = useNavigate();
  const {
    data,
    rawData,
    loading,
    fetchData,
    deleteRows,
    sortConfig,
    setSortConfig,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    selectedRows,
    setSelectedRows,
    columnValues,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
  } = useDataTable({ 
    tableName, 
    schema, 
    whereClause, 
    columns: initialColumns, 
    virtualColumns,
    sortable, 
    searchable, 
    filterable, 
    selectable,
    pageSize: initialPageSize,
    initialPage,
    limit,
    offset,
    initialData, // Pass here
    adminMode
  });

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce search term update
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(localSearchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [localSearchTerm, setSearchTerm]);

  // Sync back if external searchTerm changes (e.g. cleared)
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const { user } = useAuth();
  const { getTileStyles } = useConfig();
  const styles = getTileStyles(); // Get global styles
  const highlightColor = styles.hilight_color || user?.hilight_color || '#fbbf24';

  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [actionState, setActionState] = useState<'download' | 'upload'>('download');
  
  // New states for density and view mode
  const [density, setDensity] = useState<'compact' | 'normal' | 'wide'>('normal');
  const [viewMode, setViewMode] = useState<'list' | 'cards' | 'map'>(() => {
    if (forceGeo) {
      const saved = sessionStorage.getItem('persone_map_view_mode');
      if (saved === 'list' || saved === 'cards' || saved === 'map') {
        return saved;
      }
    }
    return 'list';
  });

  // Map state and data loading hooks
  const [mapData, setMapData] = useState<any[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapApiKey, setMapApiKey] = useState('');
  const [zoom, setZoom] = useState<number>(() => {
    if (forceGeo) {
      const saved = sessionStorage.getItem('persone_map_zoom');
      if (saved) return Number(saved);
    }
    return 12;
  });
  const [mapCenterState, setMapCenterState] = useState<{ lat: number; lng: number } | null>(() => {
    if (forceGeo) {
      const saved = sessionStorage.getItem('persone_map_center');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  });

  useEffect(() => {
    if (forceGeo) {
      sessionStorage.setItem('persone_map_view_mode', viewMode);
    }
  }, [viewMode, forceGeo]);

  useEffect(() => {
    if (forceGeo) {
      sessionStorage.setItem('persone_map_zoom', String(zoom));
    }
  }, [zoom, forceGeo]);

  useEffect(() => {
    if (forceGeo && mapCenterState) {
      sessionStorage.setItem('persone_map_center', JSON.stringify(mapCenterState));
    }
  }, [mapCenterState, forceGeo]);

  useEffect(() => {
    if (forceGeo) {
      fetch('/api/config/maps-key')
        .then(res => res.json())
        .then(data => {
          if (data && data.key) {
            setMapApiKey(data.key);
          }
        })
        .catch(err => console.error('Error fetching map key in DBTable:', err));
    }
  }, [forceGeo]);

  const fetchMapData = async () => {
    try {
      setMapLoading(true);
      const { data: viewData, error: viewError } = await supabase
        .from('cpgev_anagrafica_indirizzi_view')
        .select('*');
        
      if (viewError) throw viewError;

      const { data: indirizziData, error: indError } = await supabase
        .from('cpgev_indirizzi')
        .select('id_persona, id_tipologia_indirizzo, longitudine, latitudine');
        
      if (indError) throw indError;

      const mapped = (viewData || []).map((viewRow: any) => {
        const match = (indirizziData || []).find((ind: any) => 
          ind.id_persona === viewRow.id && 
          ind.id_tipologia_indirizzo === viewRow.ind_tipologia
        );
        return {
          ...viewRow,
          latitudine: match?.latitudine,
          longitudine: match?.longitudine
        };
      }).filter((item: any) => item.latitudine && item.longitudine);

      setMapData(mapped);
    } catch (err) {
      console.error('Error loading map data in DBTable:', err);
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'map' && forceGeo) {
      fetchMapData();
    }
  }, [viewMode, forceGeo]);

  useEffect(() => {
    if (mapData.length > 0 && !mapCenterState) {
      let sLat = 0, sLng = 0;
      let count = 0;
      mapData.forEach(m => {
        const lat = Number(m.latitudine);
        const lng = Number(m.longitudine);
        if (!isNaN(lat) && !isNaN(lng)) {
          sLat += lat;
          sLng += lng;
          count++;
        }
      });
      if (count > 0) {
        setMapCenterState({ lat: sLat / count, lng: sLng / count });
      }
    }
  }, [mapData]);

  const [filterTipologie, setFilterTipologie] = useState<number[]>(() => {
    if (forceGeo) {
      const saved = sessionStorage.getItem('persone_map_filter_tipologie');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return [];
        }
      }
    }
    return [];
  });

  useEffect(() => {
    if (forceGeo) {
      sessionStorage.setItem('persone_map_filter_tipologie', JSON.stringify(filterTipologie));
    }
  }, [filterTipologie, forceGeo]);

  const filteredMapData = useMemo(() => {
    if (filterTipologie.length === 0) return mapData;
    return mapData.filter(item => filterTipologie.includes(item.ind_tipologia));
  }, [mapData, filterTipologie]);

  const grouped = useMemo(() => {
    const cellSize = getClusterCellSize(zoom);
    const groups: Record<string, {
      items: any[];
      latSum: number;
      lngSum: number;
      lat: number;
      lng: number;
    }> = {};

    filteredMapData.forEach(item => {
      const lat = Number(item.latitudine);
      const lng = Number(item.longitudine);
      if (isNaN(lat) || isNaN(lng)) return;

      const gridX = Math.round(lng / cellSize);
      const gridY = Math.round(lat / cellSize);
      const key = `${gridX}_${gridY}`;

      if (!groups[key]) {
        groups[key] = {
          items: [],
          latSum: 0,
          lngSum: 0,
          lat: 0,
          lng: 0
        };
      }
      groups[key].items.push(item);
      groups[key].latSum += lat;
      groups[key].lngSum += lng;
    });

    return Object.values(groups).map(g => {
      const count = g.items.length;
      return {
        lat: g.latSum / count,
        lng: g.lngSum / count,
        items: g.items,
        count
      };
    });
  }, [filteredMapData, zoom]);

  const hasValidKey = Boolean(mapApiKey) && mapApiKey !== 'YOUR_API_KEY';
  const [scrolledColOffset, setScrolledColOffset] = useState(0); 
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const [pinnedOffsets, setPinnedOffsets] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState(0);

  // Sync scroll between top scrollbar and table container
  const onTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const onTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const initialColumnsKey = JSON.stringify(initialColumns);
  const columns = useMemo(() => {
    const parsedInitial = JSON.parse(initialColumnsKey);
    if (parsedInitial.length > 0) return parsedInitial;
    
    // We strictly prefer rawData (unfiltered) or a persistent set of columns
    // to prevent columns from disappearing during filtering
    if (rawData.length > 0) return Object.keys(rawData[0]);
    if (data.length > 0) return Object.keys(data[0]);
    return [];
  }, [initialColumnsKey, rawData]); // Removed 'data' from dependencies to stabilize

  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);

  const allMatches = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const ageFilter = parseAgeFilter(searchTerm);
    const matches: { rowIndex: number, col: string }[] = [];
    
    if (ageFilter) {
      data.forEach((row, rIdx) => {
        const birthVal = row.data_nascita || row.data_di_nascita || row.birth_date;
        const age = calculateAge(birthVal);
        if (evaluateAgeCondition(age, ageFilter.op, ageFilter.targetAge)) {
          matches.push({ rowIndex: rIdx, col: 'data_nascita' });
        }
      });
      return matches;
    }

    // Define which columns are searchable in cards
    const cardExcludedCols = ['foto', 'cognome', 'nome', 'id', 'regione_nascita', 'titolo_onorifico', 'tipo_scuola', 'ultimo_accesso', 'partita_iva', 'titolo_studio', 'note', 'data_inserimento', 'inserito_da', 'data_aggiornamento', 'modificato_da'];
    
    data.forEach((row, rIdx) => {
      columns.forEach(col => {
        if (!visibleColumns.includes(col)) return;
        
        // In card mode, restrict to visible fields
        if (viewMode === 'cards' && cardExcludedCols.includes(col)) return;
        
        const val = String(row[col] ?? '').toLowerCase();
        if (val.includes(searchTerm.toLowerCase())) {
          matches.push({ rowIndex: rIdx, col });
        }
      });
    });
    return matches;
  }, [data, searchTerm, columns, visibleColumns, viewMode]);

  // Reset match index when search changes
  useEffect(() => {
    setCurrentMatchIndex(allMatches.length > 0 ? 0 : -1);
  }, [allMatches.length]);

  const navigateMatches = (direction: 'next' | 'prev') => {
    if (allMatches.length === 0) return;
    setCurrentMatchIndex(prev => {
      if (direction === 'next') return (prev + 1) % allMatches.length;
      return (prev - 1 + allMatches.length) % allMatches.length;
    });
  };

  const currentMatch = allMatches[currentMatchIndex];

  // Logic to sync scroll with current match
  useEffect(() => {
    if (!currentMatch) return;

    // Autoscroll horizontally if in list mode
    if (viewMode === 'list') {
      const { col } = currentMatch;
      const colIndexInVisible = columns.filter(c => visibleColumns.includes(c)).indexOf(col);
      
      if (colIndexInVisible !== -1 && colIndexInVisible >= pinnedColumns) {
        const scrollableOffsetForCol = colIndexInVisible - pinnedColumns;
        const scrollableCount = columns.filter(c => visibleColumns.includes(c)).length - pinnedColumns;
        
        if (scrollableOffsetForCol < scrolledColOffset || scrollableOffsetForCol >= scrolledColOffset + 4) {
           setScrolledColOffset(Math.max(0, Math.min(scrollableOffsetForCol, scrollableCount - 1)));
        }
      }
    }

    // Centering the active match vertically/horizontally in the container
    setTimeout(() => {
      // Look for the element with active highlight styles
      const activeElement = document.querySelector('[class*="ring-orange-400"]');
      if (activeElement) {
        activeElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center', 
          inline: 'center' 
        });
      }
    }, 150);
  }, [currentMatch, viewMode, pinnedColumns, columns, visibleColumns]);

  React.useLayoutEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.scrollWidth);
    }
    if (tableRef.current && (pinnedColumns > 0 || selectable)) {
      const headerRow = tableRef.current.querySelector('thead tr');
      if (headerRow) {
        const cells = Array.from(headerRow.cells) as HTMLTableCellElement[];
        const offsets: number[] = [];
        let currentOffset = 0;
        
        for (let i = 0; i < cells.length; i++) {
          offsets.push(currentOffset);
          currentOffset += cells[i].getBoundingClientRect().width;
        }
        setPinnedOffsets(offsets);
      }
    }
  }, [data, columns, pinnedColumns, selectable, density, viewMode, visibleColumns, searchTerm]);

  useEffect(() => {
    if (columns.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(columns);
    }
  }, [columns, visibleColumns]);

  const toggleColumnVisibility = (col: string) => {
    setVisibleColumns(prev => 
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };

  const toggleSort = (key: string) => {
    if (!sortable) return;
    setSortConfig(prev => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getRowId = (row: any) => row.id || row.table_name || row.item || row.name;

  const toggleRowSelection = (row: any) => {
    if (!selectable) return;
    const id = getRowId(row);
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllSelection = () => {
    if (!selectable) return;
    const currentIds = data.map(row => getRowId(row));
    const allSelectedInView = currentIds.every(id => selectedRows.has(id));

    if (allSelectedInView) {
      setSelectedRows(prev => {
        const next = new Set(prev);
        currentIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedRows(prev => {
        const next = new Set(prev);
        currentIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const isGeoEnabled = useMemo(() => {
    if (forceGeo) return true;
    const colNames = columns.map(c => c.toLowerCase());
    return colNames.includes('latitudine') && colNames.includes('longitudine');
  }, [columns, forceGeo]);

  const densityConfig = {
    compact: { padding: 'p-[1px]', fontSize: 'text-[8px]', icon: Minimize2, label: 'Compressa', gap: 'gap-0.5', borderWidth: '1px' },
    normal: { padding: 'p-[3px]', fontSize: 'text-[11px]', icon: Type, label: 'Normale', gap: 'gap-4', borderWidth: '2px' },
    wide: { padding: 'p-[6px]', fontSize: 'text-xs', icon: Maximize2, label: 'Ampia', gap: 'gap-6', borderWidth: '4px' }
  };

  // Reset scrolledColOffset when search or filters change to prevent "disappearing columns"
  useEffect(() => {
    setScrolledColOffset(0);
  }, [searchTerm, filters]);

  useEffect(() => {
    if (initialPageSize) {
      setPageSize(initialPageSize);
    } else {
      setPageSize(1000);
    }
  }, [initialPageSize, setPageSize]);

  const visibleColumnsToRender = useMemo(() => {
    const filtered = columns.filter(col => visibleColumns.includes(col));
    const pinnedSet = filtered.filter((_, idx) => idx < pinnedColumns);
    const scrollableSet = filtered.filter((_, idx) => idx >= pinnedColumns);
    
    // Safety check for offset
    const currentOffset = Math.min(scrolledColOffset, Math.max(0, scrollableSet.length - 1));
    const scrolledSet = scrollableSet.slice(currentOffset);
    return [...pinnedSet, ...scrolledSet];
  }, [columns, visibleColumns, pinnedColumns, scrolledColOffset]);

  const showHorizontal = gridAxis !== 'none' && (gridAxis === 'horizontal' || gridAxis === 'both');
  const showVertical = gridAxis !== 'none' && (gridAxis === 'vertical' || gridAxis === 'both');

  const scrollHorizontal = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      const scrollableCount = columns.filter(col => visibleColumns.includes(col)).length - pinnedColumns;
      if (scrolledColOffset < scrollableCount - 1) {
        setScrolledColOffset(prev => prev + 1);
      }
    } else {
      setScrolledColOffset(prev => Math.max(0, prev - 1));
    }
  };

  const isAllSelected = useMemo(() => {
    if (data.length === 0) return false;
    return data.every(row => selectedRows.has(getRowId(row)));
  }, [data, selectedRows]);

  const handleFilterChange = (col: string, value: any) => {
    setFilters(prev => {
      const current = prev[col] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [col]: next };
    });
  };

  const getStickyColumnStyles = (index: number, isHeader = false) => {
    if (index >= pinnedColumns) return {};
    
    // Data columns start at offset 1 if selectable is true (offset 0 is checkbox)
    const offsetIndex = selectable ? index + 1 : index;
    const leftOffset = pinnedOffsets[offsetIndex] ?? 0;
    
    return {
      position: 'sticky' as const,
      left: leftOffset,
      zIndex: isHeader ? 55 : 20,
      boxShadow: index === pinnedColumns - 1 ? '2px 0 5px -2px rgba(0,0,0,0.1)' : 'none',
      backgroundColor: isHeader ? '#f8fafc' : 'white'
    };
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border border-red-100 shadow-sm">
        <div className="p-3 bg-red-50 rounded-full text-red-500 mb-4">
          <X className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">Errore nel caricamento</h3>
        <p className="text-sm text-slate-500 text-center max-w-md mb-6">
          {error.message || "Si è verificato un errore durante il recupero dei dati. Verifica i permessi o la connessione."}
        </p>
        <button 
          onClick={() => fetchData()}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Riprova
        </button>
      </div>
    );
  }

  if (loading && data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-slate-200">
        <RefreshCw className="w-8 h-8 animate-spin text-primary/50 mb-3" />
        <span className="text-sm font-medium text-slate-400">Caricamento dati in corso...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      <div className="sticky top-[-2px] z-30 bg-slate-50/95 backdrop-blur-sm px-4 py-4 border-b border-slate-200 mb-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] rounded-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon !== null && (
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                {icon || <TableIcon className="w-5 h-5" />}
              </div>
            )}
            <div>
              {title && (typeof title === 'string' ? <h2 className="text-xl font-bold text-slate-800 tracking-tight">{title}</h2> : title)}
            </div>
          </div>
          
          <div className="flex-grow flex items-center justify-center">
            {headerExtras}
          </div>

          <div className="flex items-center gap-2">
            {searchable && (
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                <input
                  type="text"
                  placeholder={searchPlaceholder || "Cerca (min. 2 car.)..."}
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  className="pl-9 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all w-full sm:w-64 shadow-sm"
                />
                {localSearchTerm && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                    {allMatches.length > 0 && (
                      <div className="flex items-center gap-1 border-r border-slate-200 pr-1.5 mr-0.5">
                        <span className="text-[10px] font-black bg-slate-800 text-white min-w-[1.5rem] h-5 px-1.5 rounded-full flex items-center justify-center shadow-lg ring-1 ring-white/20">
                          {currentMatchIndex + 1}/{allMatches.length}
                        </span>
                        <div className="flex items-center">
                          <button
                            onClick={() => navigateMatches('prev')}
                            className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-primary transition-colors"
                            title="Match precedente"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => navigateMatches('next')}
                            className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-primary transition-colors"
                            title="Match successivo"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setLocalSearchTerm('');
                        setSearchTerm('');
                      }}
                      className="p-1 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                      title="Pulisci ricerca"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
              <button
                onClick={() => setShowColumnToggle(!showColumnToggle)}
                className={cn(
                  "p-2 rounded-lg transition-colors relative",
                  showColumnToggle ? "bg-primary/10 text-primary" : "text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                )}
                title="Gestione Colonne"
              >
                <Layout className="w-5 h-5" />
                {visibleColumns.length !== columns.length && (
                  <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-primary rounded-full border border-white" />
                )}
              </button>
              <button
                onClick={() => {
                  if (onRefresh) {
                    onRefresh();
                  }
                  fetchData(true);
                }}
                className="p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 rounded-lg transition-colors"
                title="Aggiorna"
              >
                <RefreshCw className={cn("w-5 h-5", loading && "animate-spin")} />
              </button>
  
              <div className="w-[1px] h-4 bg-slate-200 mx-1" />
  
              {/* View Mode Selectors moved here, Scroll controls moved below header line */}
              <div className="flex items-center gap-0.5 bg-slate-100/50 rounded-lg p-0.5">
                {(['compact', 'normal', 'wide'] as const).map((d) => {
                  const Config = densityConfig[d];
                  return (
                    <button
                      key={d}
                      onClick={() => setDensity(d)}
                      className={cn(
                        "p-1.5 rounded-md transition-all",
                        density === d ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                      )}
                      title={`Densità ${Config.label}`}
                    >
                      <Config.icon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
  
              <div className="w-[1px] h-4 bg-slate-200 mx-1" />
  
              {/* View Mode Selectors */}
              <div className="flex items-center gap-0.5 bg-slate-100/50 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "p-1.5 rounded-md transition-all",
                    viewMode === 'list' ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                  title="Visualizzazione Elenco"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={cn(
                    "p-1.5 rounded-md transition-all",
                    viewMode === 'cards' ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                  title="Visualizzazione Schede"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  disabled={!isGeoEnabled}
                  onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
                  className={cn(
                    "p-1.5 rounded-md transition-all",
                    viewMode === 'map' ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600",
                    !isGeoEnabled && "opacity-30 cursor-not-allowed"
                  )}
                  title={isGeoEnabled ? "Visualizzazione Geografica" : "Mappa non disponibile (coordinate mancanti)"}
                >
                  <MapIcon className="w-4 h-4" />
                </button>
              </div>
              
              {selectedRows.size > 0 && (
                <>
                  <div className="w-[1px] h-4 bg-slate-200 mx-1" />
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        const nextState = actionState === 'download' ? 'upload' : 'download';
                        setActionState(nextState);
                      }}
                      className={cn(
                        "p-2 rounded-lg transition-all flex items-center gap-2",
                        actionState === 'upload' ? "bg-orange-50 text-orange-600 ring-1 ring-orange-200" : "bg-blue-50 text-blue-600 ring-1 ring-blue-200"
                      )}
                      title={actionState === 'download' ? "Passa a Modalità Carica" : "Passa a Modalità Scarica"}
                    >
                      {actionState === 'download' ? <Download className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                      <span className="text-[10px] font-black uppercase tracking-wider">{actionState === 'download' ? 'Scarica' : 'Carica'}</span>
                    </button>
  
                    <div className="flex items-center animate-in fade-in slide-in-from-left-2 duration-300">
                      <div className="w-[1px] h-4 bg-slate-200 mx-1" />
                      <button
                        onClick={() => onAction?.('csv', Array.from(selectedRows), actionState)}
                        className="p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 rounded-lg transition-colors"
                        title={actionState === 'download' ? "Esporta in CSV" : "Importa da CSV"}
                      >
                        <FileSpreadsheet className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => onAction?.('json', Array.from(selectedRows), actionState)}
                        className="p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 rounded-lg transition-colors"
                        title={actionState === 'download' ? "Esporta in JSON" : "Importa da JSON"}
                      >
                        <FileJson className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => onAction?.('sql', Array.from(selectedRows), actionState)}
                        className="p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 rounded-lg transition-colors"
                        title={actionState === 'download' ? "Esporta in SQL" : "Importa da SQL"}
                      >
                        <FileText className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
  
                  <div className="w-[1px] h-4 bg-slate-200 mx-1" />
  
                  <button
                    onClick={() => window.print()}
                    className="p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 rounded-lg transition-colors"
                    title="Stampa Selezione"
                  >
                    <Printer className="w-5 h-5" />
                  </button>
                </>
              )}
  
              <div className="w-[1px] h-4 bg-slate-200 mx-1" />
              
              {onAdd && (
                <button
                  onClick={onAdd}
                  className="p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
                  title={addLabel || "Aggiungi riga"}
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
              {selectedRows.size > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm(`Eliminare ${selectedRows.size} righe?`)) {
                      deleteRows(Array.from(selectedRows));
                    }
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Elimina selezionati"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showColumnToggle && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowColumnToggle(false)} />
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 top-14 z-50 bg-white border border-slate-200 shadow-2xl rounded-2xl p-4 w-60"
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Colonne Visibili</h4>
              <button 
                onClick={() => setVisibleColumns(columns)}
                className="text-[10px] text-primary font-bold uppercase hover:underline"
              >
                Reset
              </button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
              {columns.map(col => (
                <label key={col} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors group">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(col)}
                    onChange={() => toggleColumnVisibility(col)}
                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 truncate">
                    {columnLabels[col] || col}
                  </span>
                </label>
              ))}
            </div>
          </motion.div>
        </>
      )}

      {/* Dedicated Scroll Area between header and grid - COMMENTED PER USER REQUEST
      {viewMode === 'list' && (
        <div className="flex items-center gap-4 bg-slate-50/50 border border-slate-200 rounded-xl px-2 py-2 mt-2">
           <div 
             ref={topScrollRef}
             onScroll={onTopScroll}
             className="flex-grow overflow-x-auto overflow-y-hidden h-2.5 custom-scrollbar hover:h-4 transition-all"
           >
             <div style={{ width: tableWidth }} className="h-0.5" />
           </div>
        </div>
      )}
      */}

      {/* MAIN CONTENT RENDERING BASED ON VIEW MODE */}
      <AnimatePresence mode="wait">
        {viewMode === 'list' ? (
          <motion.div 
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="group/container bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm relative mt-2"
          >
            <div 
              ref={tableContainerRef}
              onScroll={onTableScroll}
              className="overflow-x-auto overflow-y-auto custom-scrollbar max-h-[70vh] relative"
            >
              <table ref={tableRef} className="min-w-full text-left border-separate border-spacing-0 table-auto">
                <thead className="sticky top-0 z-[45]">
                  <tr className="bg-slate-50 border-b border-slate-200 shadow-sm">
                    {selectable && (
                      <th 
                        className={cn("sticky z-50 bg-slate-50 border-b border-slate-200 w-px", densityConfig[density].padding)}
                        style={{ left: pinnedOffsets[0] ?? 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={toggleAllSelection}
                          className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                        />
                      </th>
                    )}
                    {visibleColumnsToRender.map((col, idx) => {
                      const label = columnLabels[col] || col;
                      // Replace underscores with space to allow better wrapping
                      const formattedLabel = label.replace(/_/g, ' ');

                      return (
                        <th 
                          key={col} 
                          className={cn(
                            "text-left group transition-colors hover:bg-slate-100/50",
                            idx < pinnedColumns ? "bg-slate-50" : "bg-slate-50/80",
                            showHorizontal && "border-b border-slate-200",
                            showVertical && "border-r border-slate-200",
                            densityConfig[density].padding
                          )}
                          style={{
                            ...getStickyColumnStyles(idx, true),
                            borderBottomWidth: showHorizontal ? densityConfig[density].borderWidth : 0,
                            borderRightWidth: showVertical ? densityConfig[density].borderWidth : 0
                          }}
                        >
                          <div className="flex items-center gap-2 max-w-[100px]">
                            <span 
                              className={cn(
                                "font-bold text-slate-500 uppercase tracking-wider select-none whitespace-normal leading-tight break-words", 
                                density === 'compact' ? 'text-[7px]' : 'text-[9px]',
                                sortable && "cursor-pointer hover:text-primary transition-colors"
                              )}
                              onClick={() => toggleSort(col)}
                            >
                              {formattedLabel}
                            </span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            {sortable && sortConfig?.key === col && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpWideNarrow className="w-3.5 h-3.5 text-primary" /> : 
                                <ArrowDownWideNarrow className="w-3.5 h-3.5 text-primary" />
                            )}
                            {filterable && (
                              <div className="relative">
                                <button
                                  onClick={() => setActiveFilter(activeFilter === col ? null : col)}
                                  className={cn(
                                    "p-1 rounded hover:bg-slate-200 transition-colors",
                                    filters[col]?.length > 0 ? "text-primary bg-primary/10" : "text-slate-300"
                                  )}
                                >
                                  <Filter className="w-3 h-3" />
                                </button>
                                
                                {activeFilter === col && (
                                  <div className="fixed top-auto mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-2xl z-[100] p-2 max-h-64 overflow-y-auto custom-scrollbar translate-y-2">
                                    <div className="flex items-center justify-between mb-2 px-2 pt-1">
                                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Filtra {columnLabels[col] || col}</span>
                                      <button 
                                        onClick={() => setFilters(prev => ({ ...prev, [col]: [] }))}
                                        className="text-[10px] text-primary font-bold uppercase hover:underline"
                                      >
                                        Reset
                                      </button>
                                    </div>
                                    {columnValues[col]?.map(val => (
                                      <label key={String(val)} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg cursor-pointer text-sm transition-colors group">
                                        <input
                                          type="checkbox"
                                          checked={filters[col]?.includes(val)}
                                          onChange={() => handleFilterChange(col, val)}
                                          className="rounded border-slate-300 text-primary focus:ring-primary"
                                        />
                                        <span className="truncate group-hover:text-slate-900">
                                          {val === null ? <em className="text-slate-400 text-xs">(Nessun valore)</em> : String(val)}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </th>
                      );
                    })}
                    <th className={cn("sticky right-0 z-50 bg-slate-50 border-b border-slate-200 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] w-px text-left", densityConfig[density].padding)}>
                      <span className={cn("font-bold text-slate-500 uppercase tracking-wider", densityConfig[density].fontSize)}>Azioni</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumnsToRender.length + (selectable ? 2 : 1)} className="p-12 text-center text-slate-400">
                        <div className="flex flex-col items-center">
                          <TableIcon className="w-12 h-12 mb-4 opacity-10" />
                          <p className="text-sm font-medium">Nessun dato trovato</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    data.map((row, idx) => (
                      <tr 
                        key={row.id || idx} 
                        onDoubleClick={() => onRowDoubleClick?.(row)}
                        className={cn(
                          "group transition-colors",
                          onRowDoubleClick && "cursor-pointer",
                          selectedRows.has(getRowId(row)) ? "bg-primary/5" : "hover:bg-slate-50/20"
                        )}
                      >
                        {selectable && (
                          <td 
                            className={cn("sticky z-20 bg-white group-hover:bg-slate-50 transition-colors w-px", densityConfig[density].padding)}
                            style={{ 
                              left: pinnedOffsets[0] ?? 0,
                              backgroundColor: 'white' 
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedRows.has(getRowId(row))}
                              onChange={() => toggleRowSelection(row)}
                              className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                            />
                          </td>
                        )}
                        {visibleColumnsToRender.map((col, cIdx) => (
                          <td 
                            key={col} 
                            className={cn(
                              "text-slate-600 bg-white group-hover:bg-slate-50 transition-colors whitespace-nowrap min-w-max text-left",
                              showHorizontal && "border-b border-slate-100",
                              showVertical && "border-r border-slate-100",
                              densityConfig[density].padding,
                              densityConfig[density].fontSize
                            )}
                            style={{
                              ...getStickyColumnStyles(cIdx),
                              borderBottomWidth: showHorizontal ? densityConfig[density].borderWidth : 0,
                              borderRightWidth: showVertical ? densityConfig[density].borderWidth : 0
                            }}
                          >
                            {renderers[col] ? (
                              renderers[col](row[col], row)
                            ) : typeof row[col] === 'object' ? (
                              <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 truncate max-w-[150px] inline-block">
                                {JSON.stringify(row[col])}
                              </span>
                            ) : (
                              <HighlightText 
                                text={String(row[col] ?? '')} 
                                highlight={searchTerm} 
                                color={highlightColor} 
                                isActive={currentMatch?.rowIndex === idx && currentMatch?.col === col}
                              />
                            )}
                          </td>
                        ))}
                        <td className={cn("text-left sticky right-0 z-20 bg-white group-hover:bg-slate-50 transition-colors shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] w-px", densityConfig[density].padding)}>
                          <div className="flex items-center justify-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {onView && (
                              <button
                                onClick={() => onView(row)}
                                className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                title="Visualizza"
                              >
                                <Search className="w-4 h-4" />
                              </button>
                            )}
                            {onEdit && (
                              <button
                                onClick={() => onEdit(row)}
                                className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                title="Modifica"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : viewMode === 'cards' ? (
          <motion.div 
            key="cards"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={cn(
               "grid",
               densityConfig[density].gap,
               density === 'compact' ? "grid-cols-2 md:grid-cols-4 lg:grid-cols-6" :
               density === 'normal' ? "grid-cols-1 md:grid-cols-3 lg:grid-cols-4" :
               "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            )}
          >
            {data.map((row, idx) => {
              // Extract photo/image using common field names or renderers
              const photoCol = ['foto', 'image', 'picture', 'avatar'].find(c => initialColumns.includes(c) || row[c]);
              const photoContent = photoCol && renderers[photoCol] ? renderers[photoCol](row[photoCol], row) : null;
              const directPhoto = photoCol ? row[photoCol] : null;

              return (
              <div 
                key={row.id || idx}
                onDoubleClick={() => onRowDoubleClick?.(row)}
                className={cn(
                  "bg-white border rounded-2xl shadow-sm hover:shadow-md transition-all group flex flex-col relative overflow-hidden",
                  densityConfig[density].padding,
                  densityConfig[density].gap,
                  selectedRows.has(getRowId(row)) ? "ring-2 ring-primary border-primary bg-primary/5" : "border-slate-200"
                )}
              >
                <div className="flex flex-col h-full">
                  {/* Photo and Header row */}
                  <div className={cn("flex items-center border-b border-slate-100 pb-2", densityConfig[density].gap)}>
                    {/* Avatar / Photo area */}
                    <div className="shrink-0">
                      {photoContent ? (
                        <div className={cn(
                          "rounded-lg overflow-hidden border border-slate-100 shadow-sm transition-transform group-hover:scale-105 flex items-center justify-center bg-slate-50 [&>div]:!w-full [&>div]:!h-full [&>div]:!rounded-none [&_img]:!object-cover",
                          density === 'compact' ? "w-10 h-10" : density === 'normal' ? "w-20 h-20" : "w-28 h-28"
                        )}>
                          {photoContent}
                        </div>
                      ) : directPhoto ? (
                        <div className={cn(
                          "bg-slate-100 rounded-lg overflow-hidden border border-slate-200 shrink-0",
                          density === 'compact' ? "w-10 h-10" : density === 'normal' ? "w-16 h-16" : "w-24 h-24"
                        )}>
                          <SignedImage path={directPhoto} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className={cn(
                          "bg-slate-50 flex items-center justify-center rounded-2xl border border-slate-100 text-slate-400 shrink-0",
                          density === 'compact' ? "w-10 h-10" : density === 'normal' ? "w-16 h-16" : "w-24 h-24"
                        )}>
                          <User className="w-8 h-8 opacity-20" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-grow min-w-0 flex flex-col justify-center gap-0">
                      <div className="text-[10px] font-mono font-bold text-primary/60 leading-none mb-1">ID: {row.id}</div>
                      <h3 className={cn(
                        "font-black text-slate-900 truncate uppercase tracking-tight leading-none",
                        density === 'wide' ? "text-lg" : "text-base"
                      )}>
                        {row.cognome || 'N/D'}
                      </h3>
                      <h4 className={cn(
                        "font-bold text-slate-500 truncate uppercase tracking-tight leading-tight",
                        densityConfig[density].fontSize
                      )}>
                        {row.nome || ''}
                      </h4>
                    </div>
                  </div>

                  {/* Data rows - Full width below header */}
                  <div className={cn("flex-grow space-y-0 mt-1", density === 'compact' ? "mt-0.5" : "mt-1")}>
                    {columns.filter(col => 
                      visibleColumns.includes(col) && 
                      !['foto', 'cognome', 'nome', 'id', 'regione_nascita', 'titolo_onorifico', 'tipo_scuola', 'ultimo_accesso',
                        'partita_iva', 'titolo_studio', 'note', 'data_inserimento', 'inserito_da', 'data_aggiornamento', 'modificato_da'].includes(col)
                    ).slice(0, density === 'compact' ? 7 : 15).map(col => (
                      <div key={col} className="flex items-baseline gap-1.5 border-b border-slate-50/30 last:border-0 py-0">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest shrink-0">{columnLabels[col] || col}:</span>
                        <span className={cn("text-slate-600 truncate font-semibold flex-grow", densityConfig[density].fontSize)}>
                            {renderers[col] ? renderers[col](row[col], row) : (
                              <HighlightText 
                                text={String(row[col] ?? '-')} 
                                highlight={searchTerm} 
                                color={highlightColor} 
                                isActive={currentMatch?.rowIndex === idx && currentMatch?.col === col}
                              />
                            )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={cn("mt-1 border-t border-slate-50 flex items-center justify-end gap-1", density === 'compact' ? "pt-0.5" : "pt-1")}>
                   {onView && (
                    <button
                      onClick={() => onView(row)}
                      className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  )}
                  {onEdit && (
                    <button
                      onClick={() => onEdit(row)}
                      className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </motion.div>
        ) : (
          <motion.div 
            key="map"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full"
          >
            {(() => {
              if (mapLoading) {
                return (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center min-h-[500px] text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-slate-500 text-sm font-semibold">Caricamento indirizzi sulla mappa...</p>
                  </div>
                );
              }

              if (!hasValidKey) {
                return (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center min-h-[500px] text-center">
                    <div className="p-4 bg-amber-50 text-amber-500 rounded-full mb-3 inline-block">
                      <MapIcon className="w-8 h-8" />
                    </div>
                    <h4 className="font-bold text-slate-700 text-base mb-1">Integrazione Google Maps richiesta</h4>
                    <p className="text-xs text-slate-400 max-w-xs mx-auto mb-4 leading-relaxed">
                      Configura l'API Key <code>GOOGLE_MAPS_PLATFORM_KEY</code> nei Segreti di AI Studio per abilitare la mappa geografica dell'anagrafica.
                    </p>
                  </div>
                );
              }

              return (
                <div className="flex flex-col w-full min-h-[600px] mt-2 animate-in fade-in duration-300">
                  {/* Map Canvas with Floating Locked Legend */}
                  <div className="w-full h-[600px] rounded-2.5xl overflow-hidden border border-slate-250 shadow-inner relative bg-slate-50">
                    
                    <APIProvider apiKey={mapApiKey} version="weekly">
                      <GMap
                        defaultCenter={mapCenterState || { lat: 44.4949, lng: 11.3426 }}
                        center={mapCenterState || { lat: 44.4949, lng: 11.3426 }}
                        defaultZoom={zoom}
                        zoom={zoom}
                        onCameraChanged={(ev) => {
                          setZoom(Math.round(ev.detail.zoom));
                          setMapCenterState(ev.detail.center);
                        }}
                        mapId="DBTABLE_MAP_GLOBAL"
                        internalUsageAttributionIds={['gmp_mcp_codeassist_v1_aistudio']}
                        style={{ width: '100%', height: '100%' }}
                        options={{
                          disableDefaultUI: false,
                          zoomControl: true,
                          mapTypeControl: false,
                          streetViewControl: false,
                          fullscreenControl: true
                        }}
                      >
                        <MapControl position={ControlPosition.TOP_LEFT}>
                          {/* Locked interactive legend inside MapControl for fullscreen persistence */}
                          <div className="m-4 flex flex-col gap-2 p-3 bg-white/95 backdrop-blur-md border border-slate-200 rounded-2xl shadow-xl max-w-[280px] pointer-events-auto font-sans animate-in fade-in duration-300">
                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5 mb-1">
                              <span className="text-slate-400 uppercase tracking-wider text-[10px] font-black">Filtro Tipologia:</span>
                              {filterTipologie.length > 0 && (
                                <button
                                  onClick={() => setFilterTipologie([])}
                                  className="text-[10px] font-bold text-primary hover:underline cursor-pointer bg-none border-none p-0 focus:outline-none"
                                >
                                  Reset
                                </button>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 max-h-[180px] overflow-y-auto pr-1">
                              {Object.entries(TIPOLOGIA_COLORS).map(([idStr, color]) => {
                                const id = Number(idStr);
                                const isActive = filterTipologie.length === 0 || filterTipologie.includes(id);
                                return (
                                  <button
                                    key={id}
                                    onClick={() => {
                                      setFilterTipologie(prev => {
                                        if (prev.includes(id)) {
                                          return prev.filter(x => x !== id);
                                        } else {
                                          return [...prev, id];
                                        }
                                      });
                                    }}
                                    className={cn(
                                      "flex items-center gap-2 px-2.5 py-1.5 rounded-xl border text-[10px] font-bold text-left transition-all cursor-pointer select-none w-full",
                                      isActive 
                                        ? "bg-slate-50/85 border-slate-200 text-slate-800 shadow-xs" 
                                        : "bg-slate-100/30 border-slate-100 text-slate-400 opacity-45 grayscale-[30%] hover:opacity-75"
                                    )}
                                    style={{ borderLeftWidth: '3.5px', borderLeftColor: color }}
                                  >
                                    <span className="truncate flex-grow">{TIPOLOGIA_LABELS[id] || `Tipo ${id}`}</span>
                                    {isActive && (
                                      <span className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse" style={{ backgroundColor: color }} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </MapControl>
                        {grouped.map((g, idx) => {
                          const isZoomedOut = zoom <= 13;

                          if (g.count === 1) {
                            const person = g.items[0];
                            const fullName = `${person.cognome || ''} ${person.nome || ''}`.trim();
                            const photoPath = getPersonPhotoPath(person.cognome || '', person.nome || '', true);
                            const tipColor = TIPOLOGIA_COLORS[person.ind_tipologia] || '#2563EB';

                            if (isZoomedOut) {
                              // Zoom level <= 13 (scale > 3km) use standard pin
                              return (
                                <AdvancedMarker
                                  key={`ind_std_${person.id}_${person.ind_tipologia}`}
                                  position={{ lat: g.lat, lng: g.lng }}
                                  title={fullName}
                                  onClick={() => {
                                    navigate(`/segreteria/anagrafica/persone/${person.id}?action=view`);
                                  }}
                                >
                                  <Pin background={tipColor} borderColor="#fff" glyphColor="#fff" />
                                </AdvancedMarker>
                              );
                            }

                            // Zoom level >= 14 (scale <= 3km) use photo marker
                            return (
                              <AdvancedMarker
                                key={`ind_${person.id}_${person.ind_tipologia}`}
                                position={{ lat: g.lat, lng: g.lng }}
                                title={fullName}
                                onClick={() => {
                                  navigate(`/segreteria/anagrafica/persone/${person.id}?action=view`);
                                }}
                              >
                                <div className="relative group flex items-center justify-center animate-in fade-in zoom-in-75 duration-200">
                                  <div 
                                    className="w-12 h-12 rounded-full overflow-hidden border-2 bg-white flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95 cursor-pointer"
                                    style={{ borderColor: tipColor }}
                                  >
                                    <SignedImage
                                      path={photoPath}
                                      className="w-full h-full object-cover rounded-full"
                                      alt={fullName}
                                      fallback={
                                        <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                                          {(person.cognome?.[0] || '') + (person.nome?.[0] || '')}
                                        </div>
                                      }
                                    />
                                  </div>
                                  <div 
                                    className="absolute -bottom-1 w-3 h-3 rounded-full border border-white shadow-md" 
                                    style={{ backgroundColor: tipColor }}
                                  />
                                </div>
                              </AdvancedMarker>
                            );
                          } else {
                            const dominantTip = g.items[0].ind_tipologia;
                            const clusterColor = TIPOLOGIA_COLORS[dominantTip] || '#2563EB';

                            if (isZoomedOut) {
                              // Zoom level <= 13 (scale > 3km) use standard pin for cluster with count as text
                              return (
                                <AdvancedMarker
                                  key={`cluster_std_${idx}`}
                                  position={{ lat: g.lat, lng: g.lng }}
                                  onClick={() => {
                                    setMapCenterState({ lat: g.lat, lng: g.lng });
                                    setZoom(prev => Math.min(prev + 2, 18));
                                  }}
                                >
                                  <Pin background={clusterColor} borderColor="#fff" glyphColor="#fff" glyphText={String(g.count)} />
                                </AdvancedMarker>
                              );
                            }

                            // Zoom level >= 14 (scale <= 3km) use first person photo with cluster badge ("passa alle foto")
                            const person = g.items[0];
                            const fullName = `${person.cognome || ''} ${person.nome || ''} e altri ${g.count - 1}`.trim();
                            const photoPath = getPersonPhotoPath(person.cognome || '', person.nome || '', true);
                            const tipColor = TIPOLOGIA_COLORS[person.ind_tipologia] || '#2563EB';

                            return (
                              <AdvancedMarker
                                key={`cluster_photo_${idx}`}
                                position={{ lat: g.lat, lng: g.lng }}
                                onClick={() => {
                                  setMapCenterState({ lat: g.lat, lng: g.lng });
                                  setZoom(prev => Math.min(prev + 1, 18));
                                }}
                              >
                                <div className="relative group flex items-center justify-center cursor-pointer animate-in fade-in zoom-in-75 duration-200">
                                  <div 
                                    className="w-12 h-12 rounded-full overflow-hidden border-2 bg-white flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95"
                                    style={{ borderColor: tipColor }}
                                  >
                                    <SignedImage
                                      path={photoPath}
                                      className="w-full h-full object-cover rounded-full"
                                      alt={fullName}
                                      fallback={
                                        <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                                          {g.count}
                                        </div>
                                      }
                                    />
                                  </div>
                                  <div className="absolute -top-1.5 -right-1.5 bg-red-550 border border-white rounded-full text-[9px] font-black text-white px-1.5 py-0.5 shadow-md leading-none">
                                    {g.count}
                                  </div>
                                  <div 
                                    className="absolute -bottom-1 w-3 h-3 rounded-full border border-white shadow-md" 
                                    style={{ backgroundColor: tipColor }}
                                  />
                                </div>
                              </AdvancedMarker>
                            );
                          }
                        })}
                      </GMap>
                    </APIProvider>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}

      </AnimatePresence>
        
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <div>
              Mostrando {data.length} di {totalCount} righe
              {selectedRows.size > 0 && ` • ${selectedRows.size} selezionate`}
            </div>
            
            <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
              <span>Righe per pagina:</span>
              <select 
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="bg-transparent border-none focus:ring-0 cursor-pointer font-semibold text-slate-600"
              >
                {[10, 25, 50, 100, 500, 1000, 5000, 10000].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Indietro
            </button>
            <div className="flex items-center gap-1 font-medium">
              <span>Pagina</span>
              <span className="text-slate-800">{page}</span>
              <span>di</span>
              <span className="text-slate-800">{totalPages || 1}</span>
            </div>
            <button
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
              className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Avanti
            </button>
          </div>
        </div>
      </div>
    );
};
